import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useAppSelector } from '@/store';

import AccountInformationView from '@screens/residents/account/desktop/sections/AccountInformationView';
import PaymentMethodsView from '@screens/residents/account/desktop/sections/PaymentMethodsView';
import AutopayView from '@screens/residents/account/desktop/sections/AutopayView';
import ResetPasswordView from '@screens/residents/account/desktop/sections/ResetPasswordView';
import AccountBillingView from './sections/AccountBillingView';
import NotificationSettingsView from '@screens/residents/account/desktop/sections/NotificationSettingsView';

type AccountSectionKey =
  | 'accountInformation'
  | 'paymentMethods'
  | 'autopay'
  | 'resetPassword'
  | 'notificationSettings'
  | 'accountBilling';

type NavItem = {
  key: AccountSectionKey;
  title: string;
  description: string;
};

export const DesktopAccountScreen: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [active, setActive] = useState<AccountSectionKey>('accountInformation');
  
  // Hide payment-related sections for Non-Leasing Occupants
  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';

  // Optional: read initial section from route params
  try {
    // Lazy import to avoid tight coupling to navigation types
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { useRoute } = require('@react-navigation/native');
    const route = useRoute();
    const sectionParam = route?.params?.section as AccountSectionKey | undefined;
    useEffect(() => {
      if (sectionParam && ['accountInformation','paymentMethods','autopay','resetPassword','notificationSettings'].includes(sectionParam)) {
        setActive(sectionParam);
      }
    }, [sectionParam]);
  } catch {}

  const items: NavItem[] = useMemo(() => {
    const allItems = [
      {
        key: 'accountInformation' as AccountSectionKey,
        title: 'Account Information',
        description: 'Name and email',
      },
      shouldShowPayments && {
        key: 'paymentMethods' as AccountSectionKey,
        title: 'Payment Methods',
        description: 'View, add, or remove',
      },
      shouldShowPayments && {
        key: 'autopay' as AccountSectionKey,
        title: 'Autopay',
        description: 'View, add, or remove',
      },
      {
        key: 'resetPassword' as AccountSectionKey,
        title: 'Reset Password',
        description: 'Secure your account',
      },
      {
        key: 'notificationSettings' as AccountSectionKey,
        title: 'Notification settings',
        description: 'Customize your notifications',
      },
      // {
      //   key: 'accountBilling',
      //   title: 'Billing Address',
      //   description: 'Manage your billing address',
      // },
    ].filter(Boolean) as NavItem[];
    return allItems;
  }, [shouldShowPayments]);

  const renderContent = useCallback(() => {
    switch (active) {
      case 'accountInformation':
        return <AccountInformationView />;
      case 'paymentMethods':
        return <PaymentMethodsView />;
      case 'autopay':
        return <AutopayView />;
      case 'resetPassword':
        return <ResetPasswordView />;
      case 'notificationSettings':
        return <NotificationSettingsView />;
      // case 'accountBilling':
      //   return <AccountBillingView />;
      default:
        return null;
    }
  }, [active]);

  return (
    <View style={styles.root}>
      <View style={styles.left}>
        <ScrollView contentContainerStyle={styles.leftInner} showsVerticalScrollIndicator={false}>
          {items.map((item) => (
            <TouchableOpacity
              key={item.key}
              onPress={() => setActive(item.key)}
              style={[styles.navItem, active === item.key && styles.navItemActive]}
              accessibilityRole="button"
            >
              <View style={styles.navCopy}>
                <Text style={[styles.navTitle, active === item.key && styles.navTitleActive]}>{item.title}</Text>
                <Text style={styles.navDesc}>{item.description}</Text>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
      <View style={styles.right}>{renderContent()}</View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
    padding: 16,
    gap: 16,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  left: {
    width: 360,
  },
  leftInner: {
    padding: 8,
    gap: 14,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 20,
    paddingHorizontal: 20,
    borderRadius: 16,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
  },
  navItemActive: {
    borderColor: RESIDENTS_DESKTOP_COLORS.ACCOUNT_TAB,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.ACCOUNT_TAB,
  },
  navIcon: {
    marginRight: 12,
  },
  navCopy: {
    flex: 1,
  },
  navTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 16,
    fontWeight: '800',
  },
  navTitleActive: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  navDesc: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 14,
    marginTop: 6,
  },
  right: {
    flex: 1,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
  },
});

export default DesktopAccountScreen;


